import React, {forwardRef, type ReactNode, useCallback, useImperativeHandle, useRef} from 'react';
import {type ToggleStateOptions, useToggleState} from '@react-stately/toggle';
import {useHover} from '@react-aria/interactions';
import {useButton, type AriaButtonProps} from '@react-aria/button';
import classnames from 'classnames';
import {FocusRing} from '@react-aria/focus';
import {mergeProps} from '@react-aria/utils';
import {ChevronDownIcon, WarningBadgeIcon} from '@time-webkit/all/icons';
import {useEventListener} from 'usehooks-ts';

import styles from './styles.module.css';

type ButtonProps = ToggleStateOptions & Omit<AriaButtonProps, 'onPress'> & {
    onPress?: () => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onKeyDown?: (e: React.KeyboardEvent) => void;
    'data-testid'?: string;
};

type Props = {
    id: string;
    activeIcon: ReactNode;
    inactiveIcon?: ReactNode;
    leftButtonProps?: ButtonProps;
    rightButtonProps?: ButtonProps;
    shouldCloseOnClickOutside?: boolean;
    withWarningBadge?: boolean;
}

type Refs = {
    leftButton: HTMLButtonElement | null;
    rightButton: HTMLButtonElement | null;
}

const defaultButtonProps: ButtonProps = {
    type: 'button',
};

export const SplitButton = forwardRef<Refs, Props>(({
    activeIcon,
    inactiveIcon = activeIcon,
    leftButtonProps = defaultButtonProps,
    rightButtonProps = defaultButtonProps,
    shouldCloseOnClickOutside = true,
    withWarningBadge = false,
}, ref) => {
    const leftButtonRef = useRef<HTMLButtonElement>(null);
    const rightButtonRef = useRef<HTMLButtonElement>(null);
    const leftButtonState = useToggleState(leftButtonProps);
    const rightButtonState = useToggleState(rightButtonProps);

    const leftOnPress = useCallback(() => {
        if (leftButtonProps.isDisabled) {
            return;
        }
        leftButtonProps.onPress?.();
        leftButtonState.toggle();
    }, [leftButtonProps, leftButtonState]);

    const rightOnPress = useCallback(() => {
        if (rightButtonProps.isDisabled) {
            return;
        }
        rightButtonProps.onPress?.();
        rightButtonState.toggle();
    }, [rightButtonProps, rightButtonState]);

    const {
        buttonProps: leftProps,
        isPressed: isLeftButtonPressed,
    } = useButton({...leftButtonProps, onPress: leftOnPress}, leftButtonRef);
    const {
        buttonProps: rightProps,
        isPressed: isRightButtonPressed,
    } = useButton({...rightButtonProps, onPress: rightOnPress}, rightButtonRef);

    const {
        hoverProps: leftButtonHoverProps,
        isHovered: isLeftButtonHovered,
    } = useHover({isDisabled: leftButtonProps.isDisabled});
    const {
        hoverProps: rightButtonHoverProps,
        isHovered: isRightButtonHovered,
    } = useHover({isDisabled: rightButtonProps.isDisabled});

    const handleOutsidePress = (event: MouseEvent | KeyboardEvent) => {
        if (!shouldCloseOnClickOutside || !rightButtonState.isSelected) {
            return;
        }

        if (event instanceof KeyboardEvent && ![' ', 'Enter'].includes(event.key)) {
            return;
        }

        const element = rightButtonRef.current;

        if (!element || element.contains(event.target as Node)) {
            return;
        }

        rightOnPress();
    };

    useImperativeHandle(ref, () => ({
        leftButton: leftButtonRef.current,
        rightButton: rightButtonRef.current,
    }));

    useEventListener('mousedown', handleOutsidePress, undefined, true);
    useEventListener('keydown', handleOutsidePress, undefined, true);

    return (
        <div
            className={classnames(styles.buttonGroup, {
                [styles.buttonGroupHovered]: isLeftButtonHovered || isRightButtonHovered,
                [styles.buttonGroupPressed]: isLeftButtonPressed || isRightButtonPressed,
            })}
        >
            <FocusRing focusRingClass={styles.focused}>
                <button
                    {...mergeProps(
                        leftButtonHoverProps,
                        leftProps,
                        {
                            onMouseEnter: leftButtonProps.onMouseEnter,
                            onMouseLeave: leftButtonProps.onMouseLeave,
                        },
                    )}
                    data-testid={leftButtonProps['data-testid']}
                    ref={leftButtonRef}
                    className={classnames(styles.leftButton, {
                        [styles.leftButtonActive]: leftButtonState.isSelected,
                        [styles.leftButtonHovered]: isLeftButtonHovered,
                        [styles.leftButtonPressed]: isLeftButtonPressed,
                        [styles.disabled]: leftButtonProps.isDisabled,
                    })}
                >
                    {leftButtonState.isSelected ? activeIcon : inactiveIcon}
                    {withWarningBadge && (
                        <span className={styles.warningBadgeWrapper}>
                            <WarningBadgeIcon width={12} height={12} className={styles.warningBadge} />
                        </span>
                    )}
                </button>
            </FocusRing>
            <FocusRing focusRingClass={styles.focused}>
                <button
                    {...mergeProps(
                        rightButtonHoverProps,
                        rightProps,
                        {
                            onMouseEnter: rightButtonProps.onMouseEnter,
                            onMouseLeave: rightButtonProps.onMouseLeave,
                        },
                    )}
                    data-testid={rightButtonProps['data-testid']}
                    ref={rightButtonRef}
                    className={classnames(styles.rightButton, {
                        [styles.disabled]: rightButtonProps.isDisabled,
                    })}
                >
                    <ChevronDownIcon
                        data-testid={
                            rightButtonProps['data-testid'] ? `${rightButtonProps['data-testid']}--chevron` : undefined
                        }
                        className={classnames(styles.rightButtonIcon, {
                            [styles.rightButtonIconActive]: rightButtonState.isSelected,
                            [styles.rightButtonHovered]: isRightButtonHovered,
                            [styles.rightButtonPressed]: isRightButtonPressed,
                        })} width={20} height={20}
                    />
                </button>
            </FocusRing>
        </div>
    );
});
